import * as React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';

export interface ConfirmOptions {
  title: string;
  description?: React.ReactNode;
  /** Label of the confirming button; defaults to "Confirm". */
  actionLabel?: string;
  cancelLabel?: string;
  /** Paints the confirming button as destructive. */
  destructive?: boolean;
}

/** Resolves `true` when the user confirms, `false` on cancel, Escape, or a newer request. */
export type ConfirmFn = (request: ConfirmOptions | string) => Promise<boolean>;

interface PendingConfirm {
  options: ConfirmOptions;
  resolve: (confirmed: boolean) => void;
  /** The control that asked, so focus returns there; there is no Radix trigger to do it. */
  returnFocus: HTMLElement | null;
}

const ConfirmContext = React.createContext<ConfirmFn | null>(null);

/**
 * True while an open alert dialog owns the keyboard: the event comes from inside one, or one
 * is open and focus has fallen back to the body (a scrim click). Page-level shortcut and
 * Escape listeners return early on it: the native prompt used to block them, the kit dialog
 * does not. `usePrompt` sits on the same role for exactly this reason.
 */
export function isInsideAlertDialog(target: EventTarget | null): boolean {
  if (target instanceof Element && target.closest('[role="alertdialog"]') !== null) return true;
  return document.querySelector('[role="alertdialog"][data-state="open"]') !== null;
}

/**
 * Hosts the single confirm dialog behind `useConfirm`. `UiProvider` mounts it, so every shell and
 * `renderInForgeRoot` test can call `await confirm('Mark this task done?')` where `window.confirm`
 * used to sit, and get a themed, testable Alert Dialog instead of the native prompt.
 */
export function ConfirmProvider({ children }: React.PropsWithChildren) {
  // `pending` outlives `open` so the dialog keeps its content through the exit animation.
  const [pending, setPending] = React.useState<PendingConfirm | null>(null);
  const [open, setOpen] = React.useState(false);
  const pendingRef = React.useRef<PendingConfirm | null>(null);

  const settle = React.useCallback((confirmed: boolean) => {
    const current = pendingRef.current;
    pendingRef.current = null;
    setOpen(false);
    current?.resolve(confirmed);
  }, []);

  React.useEffect(
    () => () => {
      pendingRef.current?.resolve(false);
      pendingRef.current = null;
    },
    []
  );

  const confirm = React.useCallback<ConfirmFn>((request) => {
    const options = typeof request === 'string' ? { title: request } : request;
    return new Promise<boolean>((resolve) => {
      pendingRef.current?.resolve(false);
      // `document.body` is an HTMLElement and is always connected, so capturing it would make
      // the handler below preventDefault and focus <body> — exactly the drop this avoids.
      const active = document.activeElement;
      const returnFocus = active instanceof HTMLElement && active !== document.body ? active : null;
      const next = { options, resolve, returnFocus };
      pendingRef.current = next;
      setPending(next);
      setOpen(true);
    });
  }, []);

  const options = pending?.options;
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={open}
        onOpenChange={(open) => {
          if (!open) settle(false);
        }}
      >
        {options ? (
          <AlertDialogContent
            size="sm"
            onCloseAutoFocus={(event) => {
              const target = pending?.returnFocus;
              if (target?.isConnected) {
                event.preventDefault();
                target.focus();
              }
            }}
            {...(options.description ? {} : { 'aria-describedby': undefined })}
          >
            <AlertDialogHeader>
              <AlertDialogTitle>{options.title}</AlertDialogTitle>
              {options.description ? (
                <AlertDialogDescription>{options.description}</AlertDialogDescription>
              ) : null}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => settle(false)}>
                {options.cancelLabel ?? 'Cancel'}
              </AlertDialogCancel>
              <AlertDialogAction
                variant={options.destructive ? 'destructive' : 'default'}
                onClick={() => settle(true)}
              >
                {options.actionLabel ?? 'Confirm'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        ) : null}
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const confirm = React.useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm needs a UiProvider above it (every shell and renderInForgeRoot).');
  }
  return confirm;
}
